/**
 * Coordal API Service
 * 
 * Handles calls to our Coordal API for geocoding, search, and other services.
 * This replaces direct Mapbox geocoding with our Ghana-specific data.
 */

import { API_URL } from '../lib/env';
import { shouldFetchPendingSurveyGeometries } from '../utils/pendingSurveyGeometries';

// API base URL - configured via VITE_API_URL env var
const API_BASE_URL = API_URL;

/** Bearer headers for endpoints that require sign-in. */
export function getAuthBearerHeaders(includeJsonContentType = false): Record<string, string> {
  const headers: Record<string, string> = {};
  if (includeJsonContentType) {
    headers['Content-Type'] = 'application/json';
  }
  const token = localStorage.getItem('auth_token');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

export function requireAuthToken(): string {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    throw new Error('Please sign in to use this feature.');
  }
  return token;
}

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export interface AddressComponents {
  building_number?: string;
  street?: string;
  suburb?: string;
  city?: string;
  district?: string;
  region?: string;
  postcode?: string;
}

export interface GeocodingResult {
  formatted_address: string;
  coordinates: Coordinates;
  address_components: AddressComponents;
  confidence: number;
  match_type: 'exact' | 'interpolated' | 'approximate';
}

export interface SearchResult {
  id: string;
  type: 'place' | 'address' | 'street' | 'building' | 'poi' | 'country' | 'transit_stop' | 'airport' | 'aeroway' | 'natural' | 'water';
  name: string;
  formatted_address?: string;
  coordinates: Coordinates;
  relevance?: number;
  category?: string;
  address?: string;  // Street address (e.g., "High Street, Accra")
  district?: string;  // District, Region (e.g., "Accra Metropolitan, Greater Accra")
  postcode?: string;  // Full postcode for copy (e.g., "YWA1B B0252")
  postcode_display?: string;  // Short postcode for display (e.g., "A1B B0252")
  ghana_post?: string;  // Ghana Post code (e.g., "GA-489-2201")
  opening_hours_status?: string | null;
  distance_meters?: number;
  flood_risk_level?: string | null;
  flood_prone?: boolean | null;
  avg_rainfall_mm?: number | null;
  seasonal_access_notes?: string | null;
}

export interface AutocompleteResult {
  id: string;
  text: string;
  type: 'place' | 'address' | 'street' | 'building' | 'poi' | 'boundary' | 'postcode' | 'frontage' | 'country' | 'transit_stop' | 'airport' | 'aeroway' | 'natural' | 'water';
  category?: string | null;
  address?: string | null;  // Street address (e.g., "High Street, Accra")
  ghana_post?: string;
  street?: string;
  neighborhood?: string;
  district?: string;  // District, Region (e.g., "Accra Metropolitan, Greater Accra")
  postcode?: string;  // Full postcode for copy (e.g., "YWA1B B0252")
  postcode_display?: string;  // Short postcode for display (e.g., "A1B B0252")
  opening_hours_status?: string | null;
  distance_meters?: number;  // Distance from user location
  coordinates?: Coordinates;
  flood_risk_level?: string | null;
  flood_prone?: boolean | null;
  avg_rainfall_mm?: number | null;
  seasonal_access_notes?: string | null;
  highlight?: {
    start: number;
    end: number;
  }[];
}

export interface ReverseGeocodeResult {
  formatted_address: string;
  coordinates: Coordinates;
  address_components: AddressComponents;
  distance_meters?: number;
}

// Category types for dynamic icon/color rendering
export interface SubcategoryItem {
  id: string;
  name: string;
  icon: string;  // Lucide icon name
  color: string;  // Tailwind color
}

export interface Category {
  id: string;
  name: string;
  icon: string;  // Lucide icon name
  color: string;  // Tailwind color
  subcategories: SubcategoryItem[];
}

export interface CategoriesResponse {
  status: string;
  categories: Category[];
}

export interface GeoJSONGeometry {
  type: 'Point' | 'Polygon' | 'MultiPolygon' | 'LineString';
  coordinates: number[] | number[][] | number[][][] | number[][][][];
}

export interface PlaceAmenities {
  wheelchair?: string | null;
  internet_access?: boolean | null;
  air_conditioning?: boolean | null;
  outdoor_seating?: boolean | null;
  delivery?: boolean | null;
  takeaway?: boolean | null;
  parking?: string | null;
  drive_through?: boolean | null;
}

export interface PlaceDetails {
  id: string;
  name: string;
  type: string;
  category?: string;
  formatted_address: string;
  street_address?: string;
  house_number?: string;
  frontage_street_address?: string;
  addr_street?: string;
  addr_suburb?: string;
  addr_city?: string;
  district?: string;
  region?: string;
  ghana_post_code?: string;
  ghana_post_gps?: string;
  gss_pcode?: string;
  admin_source?: string;
  admin_level?: number;
  coordinates: Coordinates;
  geometry?: GeoJSONGeometry;  // Building polygon for highlighting
  phone?: string;
  mobile?: string;
  whatsapp?: string;
  email?: string;
  website?: string;
  facebook?: string;
  instagram?: string;
  hours?: {
    is_open?: boolean | null;
    open?: string | null;
    close?: string | null;
    timezone?: string;
  };
  opening_hours?: string | null;
  opening_hours_status?: string | null;
  rating?: number;
  reviewCount?: number;
  review_count?: number;
  photos?: string[];
  description?: string;
  description_source_url?: string;
  brand?: string;
  cuisine?: string;
  amenities?: PlaceAmenities | null;
  building_levels?: number;
  building_units?: { label: string; floor: number }[];
  stop_ref?: string | null;
  routes?: string[];
  operators?: string[];
  shelter?: boolean | null;
  bench?: boolean | null;
  lighting?: boolean | null;
  flood_risk_level?: string | null;
  flood_prone?: boolean | null;
  avg_rainfall_mm?: number | null;
  seasonal_access_notes?: string | null;
}

/**
 * API Error class
 */
export class APIError extends Error {
  code: string;
  status: number;
  retryAfter?: number;

  constructor(message: string, code: string, status: number, retryAfter?: number) {
    super(message);
    this.code = code;
    this.status = status;
    this.retryAfter = retryAfter;
    this.name = 'APIError';
  }

  /**
   * Check if this is a rate limit error
   */
  isRateLimitError(): boolean {
    return this.status === 429;
  }

  /**
   * Get user-friendly error message
   */
  getUserMessage(): string {
    if (this.status === 429) {
      const minutes = this.retryAfter ? Math.ceil(this.retryAfter / 60) : 5;
      return `You've made too many requests. Please wait ${minutes} minute${minutes > 1 ? 's' : ''} or sign in for higher limits.`;
    }
    if (this.status === 401 || this.status === 403) {
      return 'Please sign in to access this feature.';
    }
    if (this.status === 404) {
      return 'The requested resource was not found.';
    }
    if (this.status >= 500) {
      return 'Server error. Please try again later.';
    }
    return this.message;
  }
}

/**
 * Parse FastAPI / generic error JSON into a user-visible message.
 */
function messageFromApiErrorPayload(data: unknown): string {
  if (!data || typeof data !== 'object') return 'Request failed';
  const d = data as Record<string, unknown>;
  if (typeof d.detail === 'string') return d.detail;
  if (Array.isArray(d.detail)) {
    const first = d.detail[0];
    if (typeof first === 'string') return first;
    if (first && typeof first === 'object' && 'msg' in first) {
      const msg = (first as { msg?: string }).msg;
      if (typeof msg === 'string') return msg;
    }
  }
  const err = d.error;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object' && 'message' in err) {
    const m = (err as { message?: string }).message;
    if (typeof m === 'string') return m;
  }
  return 'Request failed';
}

/**
 * Make API request with error handling
 */
let isRefreshing = false;

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  _isRetry = false
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Add auth token if available
  const token = localStorage.getItem('auth_token');
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      // Auto-refresh token on 401 (expired token) — retry once
      if (response.status === 401 && !_isRetry && !isRefreshing) {
        const refresh = localStorage.getItem('refresh_token');
        if (refresh) {
          isRefreshing = true;
          try {
            const refreshRes = await fetch(`${API_BASE_URL}/console/auth/refresh`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refresh_token: refresh }),
            });
            const refreshData = await refreshRes.json();
            const refreshPayload = refreshData.data ?? refreshData;
            if (refreshRes.ok && refreshPayload.token) {
              localStorage.setItem('auth_token', refreshPayload.token);
              isRefreshing = false;
              // Retry the original request with the new token
              return apiRequest<T>(endpoint, options, true);
            }
          } catch {
            // refresh failed — fall through to error
          } finally {
            isRefreshing = false;
          }
        }
      }

      // Extract retry-after header for rate limit errors
      const retryAfter = response.status === 429
        ? parseInt(response.headers.get('Retry-After') || '60', 10)
        : undefined;

      throw new APIError(
        messageFromApiErrorPayload(data),
        (data as { error?: { code?: string } })?.error?.code || 'UNKNOWN_ERROR',
        response.status,
        retryAfter
      );
    }

    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      'Network error. Please check your connection.',
      'NETWORK_ERROR',
      0
    );
  }
}

// ============================================================================
// Geocoding API
// ============================================================================

/**
 * Forward geocode - convert address to coordinates
 */
export async function geocode(
  address: string,
  options: { limit?: number; country?: string } = {}
): Promise<GeocodingResult[]> {
  const { limit = 5, country = 'GH' } = options;

  const response = await apiRequest<{ status: string; data: GeocodingResult[] }>(
    '/v1/geocode',
    {
      method: 'POST',
      body: JSON.stringify({ address, limit, country }),
    }
  );

  return response.data;
}

/**
 * Reverse geocode - convert coordinates to address
 */
export async function reverseGeocode(
  latitude: number,
  longitude: number,
  options: { language?: string; adminOnly?: boolean } = {}
): Promise<ReverseGeocodeResult> {
  const { language = 'en', adminOnly = false } = options;

  const response = await apiRequest<{ status: string; data: ReverseGeocodeResult }>(
    '/v1/reverse-geocode',
    {
      method: 'POST',
      body: JSON.stringify({ latitude, longitude, language, admin_only: adminOnly }),
    }
  );

  return response.data;
}

/** Contribution Add Details only — richer suburb/city with nearest-in-district fallback. */
export async function reverseGeocodeForContribution(
  latitude: number,
  longitude: number,
): Promise<{ result: ReverseGeocodeResult | null; suggestedFields: string[] }> {
  const response = await apiRequest<{
    status: string;
    data: ReverseGeocodeResult | null;
    suggested_fields?: string[];
  }>(
    '/v1/contribution/reverse-geocode',
    {
      method: 'POST',
      body: JSON.stringify({ latitude, longitude }),
    }
  );

  return {
    result: response.data,
    suggestedFields: response.suggested_fields ?? [],
  };
}

/** Administrative boundary row from GET /v1/regions, /v1/districts, /v1/towns */
export interface AdminBoundaryInfo {
  id: string;
  name: string;
  name_en?: string | null;
  admin_level: number;
  code?: string | null;
  population?: number | null;
  area_km2?: number | null;
  feature_type?: string | null;
}

/**
 * Regions (admin_level=1). Optional name filter.
 */
export async function listRegions(q?: string): Promise<AdminBoundaryInfo[]> {
  const params = new URLSearchParams();
  if (q?.trim()) params.set('q', q.trim());
  const qs = params.toString();
  const response = await apiRequest<{ status: string; data: AdminBoundaryInfo[] }>(
    `/v1/regions${qs ? `?${qs}` : ''}`
  );
  return response.data ?? [];
}

/**
 * Districts (admin_level=2). Optional region filter and name search.
 */
export async function listDistricts(region: string | undefined, q?: string): Promise<AdminBoundaryInfo[]> {
  const params = new URLSearchParams();
  if (region?.trim()) params.set('region', region.trim());
  if (q?.trim()) params.set('q', q.trim());
  const qs = params.toString();
  const response = await apiRequest<{ status: string; data: AdminBoundaryInfo[] | { region?: string; districts: AdminBoundaryInfo[] } }>(
    `/v1/districts${qs ? `?${qs}` : ''}`
  );
  const d = response.data;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.districts)) return d.districts;
  return [];
}

/**
 * Cities / towns / suburbs (admin_level 3–6). Requires at least 2 characters.
 */
export async function searchTowns(
  query: string,
  options: { district?: string; city?: string } = {}
): Promise<AdminBoundaryInfo[]> {
  const params = new URLSearchParams();
  if (query.trim()) params.set('q', query.trim());
  if (options.district?.trim()) params.set('district', options.district.trim());
  if (options.city?.trim()) params.set('city', options.city.trim());
  const response = await apiRequest<{ status: string; data: AdminBoundaryInfo[] }>(
    `/v1/towns?${params.toString()}`
  );
  return response.data ?? [];
}

/**
 * Areas / suburbs / neighbourhoods (admin_level 7–9). Works like searchTowns.
 */
export async function searchSuburbs(
  query: string,
  options: { district?: string; city?: string } = {}
): Promise<AdminBoundaryInfo[]> {
  const params = new URLSearchParams();
  if (query.trim()) params.set('q', query.trim());
  if (options.district?.trim()) params.set('district', options.district.trim());
  if (options.city?.trim()) params.set('city', options.city.trim());
  params.set('min_level', '7');
  params.set('max_level', '9');
  const response = await apiRequest<{ status: string; data: AdminBoundaryInfo[] }>(
    `/v1/towns?${params.toString()}`
  );
  return response.data ?? [];
}

// ============================================================================
// Search API
// ============================================================================

/**
 * Search for places, addresses, and buildings
 */
export async function search(
  query: string,
  options: {
    limit?: number;
    types?: string[];
    bounds?: { sw: Coordinates; ne: Coordinates };
    proximity?: Coordinates;
  } = {}
): Promise<SearchResult[]> {
  const { limit = 10 } = options;

  const params = new URLSearchParams({
    q: query,
    limit: limit.toString(),
  });

  if (options.types) {
    params.append('types', options.types.join(','));
  }

  if (options.bounds) {
    params.append(
      'bounds',
      `${options.bounds.sw.latitude},${options.bounds.sw.longitude},${options.bounds.ne.latitude},${options.bounds.ne.longitude}`
    );
  }

  if (options.proximity) {
    params.append('proximity', `${options.proximity.latitude},${options.proximity.longitude}`);
  }

  const response = await apiRequest<{ status: string; data: SearchResult[] }>(
    `/map/search?${params}`
  );

  return response.data;
}

/**
 * Get autocomplete suggestions
 */
export interface AutocompleteResponse {
  suggestions: AutocompleteResult[];
  total: number;
  offset: number;
  has_more: boolean;
}

export async function autocomplete(
  query: string,
  options: {
    limit?: number;
    offset?: number;
    types?: string[];
    proximity?: Coordinates;
    searchType?: string;
  } = {}
): Promise<AutocompleteResponse> {
  const { limit = 8, offset = 0 } = options;

  const params = new URLSearchParams({
    q: query,
    limit: limit.toString(),
    offset: offset.toString(),
  });

  if (options.types) {
    params.append('types', options.types.join(','));
  }

  if (options.proximity) {
    params.append('lat', options.proximity.latitude.toString());
    params.append('lng', options.proximity.longitude.toString());
  }

  if (options.searchType && options.searchType !== 'all') {
    params.append('search_type', options.searchType);
  }

  try {
    const response = await apiRequest<{ status: string; suggestions: AutocompleteResult[]; total: number; offset: number; has_more: boolean }>(
      `/map/autocomplete?${params}`
    );
    return {
      suggestions: response.suggestions,
      total: response.total ?? 0,
      offset: response.offset ?? 0,
      has_more: response.has_more ?? false,
    };
  } catch (error) {
    if (error instanceof APIError && error.isRateLimitError()) {
      throw error;
    }
    console.error('Autocomplete error:', error);
    return { suggestions: [], total: 0, offset: 0, has_more: false };
  }
}

// ============================================================================
// Categories API
// ============================================================================

/**
 * Get available POI categories with icons and colors
 * Results are cached by the backend for 1 hour
 */
export async function getCategories(): Promise<Category[]> {
  const response = await apiRequest<CategoriesResponse>('/map/categories');
  return response.categories;
}

// ============================================================================
// Place Details API
// ============================================================================

/**
 * Get place details by ID
 */
export async function getPlaceDetails(placeId: string): Promise<PlaceDetails> {
  const response = await apiRequest<{ status: string; place: PlaceDetails }>(
    `/map/place/${placeId}`
  );

  return response.place;
}

/**
 * Update place details (community edit). Only non-undefined fields are sent.
 */
export async function updatePlaceDetails(
  placeId: string,
  data: {
    phone?: string;
    mobile?: string;
    whatsapp?: string;
    email?: string;
    website?: string;
    facebook?: string;
    instagram?: string;
    opening_hours?: string;
    ghana_post_gps?: string;
  }
): Promise<PlaceDetails> {
  const response = await apiRequest<{ status: string; place: PlaceDetails }>(
    `/map/place/${placeId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    }
  );
  return response.place;
}

/**
 * Submit a rating for a place (1-5 stars).
 */
export async function ratePlace(
  placeId: string,
  rating: number
): Promise<{ new_rating: number; new_count: number; user_rating: number }> {
  return apiRequest<{ status: string; new_rating: number; new_count: number; user_rating: number }>(
    `/map/place/${placeId}/rate`,
    {
      method: 'POST',
      body: JSON.stringify({ rating }),
    }
  );
}

/**
 * Get location details by coordinates.
 * @param mode `map` (default) — map click with nearby POIs; `building` — Find My Postcode lookup
 */
export async function getLocationDetails(
  latitude: number,
  longitude: number,
  options?: { mode?: 'map' | 'building' }
): Promise<{
  coordinates: Coordinates;
  address: string;
  postcode?: string;
  postcode_display?: string;
  ghana_post_gps?: string;
  district?: string;
  region?: string;
  place_id?: string;
  nearby_places: { name: string; distance_meters: number }[];
}> {
  const response = await apiRequest<{
    status: string;
    location: {
      coordinates: Coordinates;
      address: string;
      postcode?: string;
      postcode_display?: string;
      ghana_post_gps?: string;
      district?: string;
      region?: string;
      place_id?: string;
      nearby_places: { name: string; distance_meters: number }[];
    };
  }>(`/map/location/${latitude}/${longitude}${options?.mode === 'building' ? '?mode=building' : ''}`);

  return response.location;
}

// ============================================================================
// Building & Postcode API
// ============================================================================

/**
 * Get building details by code
 */
export async function getBuildingByCode(code: string): Promise<{
  postcode?: string;
  address: {
    formatted: string;
    building_number?: string;
    street?: string;
    suburb?: string;
    city?: string;
    postcode?: string;
  };
  coordinates: Coordinates;
  building_type?: string;
  floors?: number;
  units?: number;
}> {
  const response = await apiRequest<{ status: string; postcode?: string; address: any; coordinates: Coordinates; building_type?: string; floors?: number; units?: number }>(
    `/v1/building/${code}`
  );

  return {
    postcode: response.postcode,
    address: response.address,
    coordinates: response.coordinates,
    building_type: response.building_type,
    floors: response.floors,
    units: response.units,
  };
}

/**
 * Get postcode details
 */
export async function getPostcodeDetails(code: string): Promise<{
  postcode: string;
  type: string;
  region: string;
  district: string;
  bounds: { southwest: Coordinates; northeast: Coordinates };
  center: Coordinates;
  area_km2: number;
  building_count: number;
}> {
  const response = await apiRequest<{
    status: string;
    postcode: string;
    type: string;
    region: string;
    district: string;
    bounds: { southwest: Coordinates; northeast: Coordinates };
    center: Coordinates;
    area_km2: number;
    building_count: number;
  }>(`/v1/postcode/${code}`);

  return {
    postcode: response.postcode,
    type: response.type,
    region: response.region,
    district: response.district,
    bounds: response.bounds,
    center: response.center,
    area_km2: response.area_km2,
    building_count: response.building_count,
  };
}

// ============================================================================
// Public Map API (no auth required)
// ============================================================================

/**
 * Public search endpoint (limited results, no auth)
 * Returns results from /map/search API endpoint
 */
export async function publicSearch(
  query: string,
  limit: number = 5,
  searchType: string = 'all',
  proximity?: { latitude: number; longitude: number }
): Promise<SearchResult[]> {
  const params = new URLSearchParams({
    q: query,
    limit: limit.toString(),
  });

  if (searchType && searchType !== 'all') {
    params.append('search_type', searchType);
  }

  if (proximity) {
    params.append('lat', proximity.latitude.toString());
    params.append('lng', proximity.longitude.toString());
  }

  const response = await apiRequest<{
    status: string; data: Array<{
      id?: string;
      name: string;
      type: string;
      category?: string;
      coordinates: Coordinates;
      address?: string;
      district?: string;
      postcode?: string;
      postcode_display?: string;
      ghana_post?: string | null;
      distance_meters?: number;
      flood_risk_level?: string | null;
      flood_prone?: boolean | null;
      avg_rainfall_mm?: number | null;
      seasonal_access_notes?: string | null;
    }>
  }>(
    `/map/search?${params}`
  );

  // Transform API response to frontend SearchResult format
  return response.data.map((r, index) => ({
    id: r.id || `result_${index}`,
    type: r.type as 'place' | 'address' | 'street' | 'building' | 'poi',
    name: r.name,
    formatted_address: r.address || '',
    address: r.address,
    district: r.district,
    postcode: r.postcode,
    postcode_display: r.postcode_display,
    ghana_post: r.ghana_post || undefined,
    coordinates: r.coordinates,
    relevance: 1 - (index * 0.1), // Approximate relevance based on order
    category: r.category,
    distance_meters: r.distance_meters,
    flood_risk_level: r.flood_risk_level ?? null,
    flood_prone: r.flood_prone ?? null,
    avg_rainfall_mm: r.avg_rainfall_mm ?? null,
    seasonal_access_notes: r.seasonal_access_notes ?? null,
  }));
}

// getCategories is defined above with proper Category types

/**
 * Get autocomplete suggestions
 */
export async function getAutocompleteSuggestions(
  query: string,
  limit: number = 5
): Promise<Array<{
  id: string;
  text: string;
  type: string;
  category?: string;
  coordinates?: Coordinates;
  address?: string;
}>> {
  const params = new URLSearchParams({
    q: query,
    limit: limit.toString(),
  });

  const response = await apiRequest<{
    status: string;
    suggestions: Array<{
      id: string;
      text: string;
      type: string;
      category?: string;
      coordinates?: Coordinates;
      address?: string;
    }>;
  }>(`/map/autocomplete?${params}`);

  return response.suggestions;
}

// ============================================================================
// Authenticated User API
// ============================================================================

export interface SavedPlace {
  id: string;
  place_id: string;
  label: 'home' | 'work' | 'saved';
  name: string;
  address: string;
  coordinates: Coordinates;
  created_at: string;
}

export interface RecentSearch {
  id: string;
  query: string;
  timestamp: string;
  result?: {
    id: string;
    name: string;
    address: string;
    opening_hours_status?: string | null;
    coordinates: Coordinates;
  };
}

/**
 * Get user's saved places (requires auth)
 */
export async function getSavedPlaces(): Promise<SavedPlace[]> {
  const response = await apiRequest<{
    status: string;
    places: SavedPlace[];
  }>('/map/user/places');

  return response.places;
}

/**
 * Save a place (requires auth)
 */
export async function savePlace(data: {
  place_id: string;
  label: 'home' | 'work' | 'saved';
  name: string;
  address: string;
  coordinates: Coordinates;
}): Promise<SavedPlace> {
  const response = await apiRequest<{
    status: string;
    place: SavedPlace;
  }>('/map/user/places', {
    method: 'POST',
    body: JSON.stringify(data),
  });

  return response.place;
}

/**
 * Delete a saved place (requires auth)
 */
export async function deleteSavedPlace(placeId: string): Promise<void> {
  await apiRequest<{ status: string; message: string }>(
    `/map/user/places/${placeId}`,
    { method: 'DELETE' }
  );
}

/**
 * Get user's recent searches (requires auth)
 */
export async function getRecentSearches(limit: number = 20): Promise<RecentSearch[]> {
  const response = await apiRequest<{
    status: string;
    recent: RecentSearch[];
  }>(`/map/user/recent?limit=${limit}`);

  return response.recent ?? [];
}

/**
 * Save a single recent search to the server (requires auth). Silently ignored on failure.
 */
export async function saveRecentSearch(data: {
  query: string;
  result_id?: string;
  result_name?: string;
  result_address?: string;
  result_opening_hours_status?: string;
}): Promise<void> {
  await apiRequest<{ status: string; message: string }>('/map/user/recent', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Clear recent searches (requires auth)
 */
export async function clearRecentSearches(): Promise<void> {
  await apiRequest<{ status: string; message: string }>(
    '/map/user/recent',
    { method: 'DELETE' }
  );
}

// ============================================================================
// Authentication API
// ============================================================================

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  tier: string;
}

export interface LoginResponse {
  status: string;
  token?: string;
  refresh_token?: string;
  user?: AuthUser;
  requires_2fa?: boolean;
  two_factor_token?: string;
}

export interface RegisterResponse {
  status: string;
  message: string;
  user_id: string;
  email: string;
}

/**
 * Login user
 */
export async function login(email: string, password: string): Promise<LoginResponse> {
  const raw = await apiRequest<{ status: string; data: LoginResponse; meta: unknown }>('/console/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

  // Unwrap envelope — token lives inside data
  const response = raw.data ?? raw as unknown as LoginResponse;

  // If 2FA is required, don't store tokens yet — return early
  if (response.requires_2fa && response.two_factor_token) {
    return response;
  }

  // Store token for future requests
  if (response.token) {
    localStorage.setItem('auth_token', response.token);
    if (response.refresh_token) {
      localStorage.setItem('refresh_token', response.refresh_token);
    }
  }

  return response;
}

/**
 * Verify 2FA code during login
 */
export async function verify2FALogin(twoFactorToken: string, code: string): Promise<LoginResponse> {
  const raw = await apiRequest<{ status: string; data: LoginResponse; meta: unknown }>('/console/auth/verify-2fa', {
    method: 'POST',
    body: JSON.stringify({ two_factor_token: twoFactorToken, code }),
  });

  const response = raw.data ?? raw as unknown as LoginResponse;

  if (response.token) {
    localStorage.setItem('auth_token', response.token);
    if (response.refresh_token) {
      localStorage.setItem('refresh_token', response.refresh_token);
    }
  }

  return response;
}

/**
 * Register new user
 */
export async function register(data: {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  user_type?: string;
  organization_name?: string;
}): Promise<RegisterResponse> {
  const response = await apiRequest<RegisterResponse>('/console/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  });

  return response;
}

/**
 * Logout user
 */
export async function logout(): Promise<void> {
  try {
    await apiRequest<{ status: string; message: string }>('/console/auth/logout', {
      method: 'POST',
    });
  } finally {
    // Clear tokens regardless of API response
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
  }
}

/**
 * Request password reset email
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await apiRequest<{ status: string; message: string }>('/console/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ email, source: 'public_map' }),
  });
}

/**
 * Refresh access token
 */
export async function refreshToken(): Promise<{ token: string }> {
  const refresh = localStorage.getItem('refresh_token');
  if (!refresh) {
    throw new APIError('No refresh token available', 'NO_REFRESH_TOKEN', 401);
  }

  const raw = await apiRequest<{ status: string; data: LoginResponse; meta: unknown }>('/console/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ refresh_token: refresh }),
  });

  const response = raw.data ?? raw as unknown as LoginResponse;

  if (response.token) {
    localStorage.setItem('auth_token', response.token);
    if (response.refresh_token) {
      localStorage.setItem('refresh_token', response.refresh_token);
    }
  }

  return { token: response.token || '' };
}

/**
 * Get current user info (includes two_factor_enabled)
 */
export async function getCurrentUser(): Promise<AuthUser & { two_factor_enabled?: boolean }> {
  const raw = await apiRequest<{ status: string; data: { user: AuthUser; two_factor_enabled?: boolean }; meta: unknown }>('/console/auth/me');
  const payload = raw.data ?? raw as unknown as { user: AuthUser; two_factor_enabled?: boolean };
  return { ...payload.user, two_factor_enabled: payload.two_factor_enabled };
}

// ============================================================================
// Two-Factor Authentication API
// ============================================================================

export interface Enable2FAData {
  secret: string;
  qr_code_url: string;
  backup_codes: string[];
}

/**
 * Enable 2FA — generates TOTP secret and QR code
 */
export async function enable2FA(): Promise<Enable2FAData> {
  const raw = await apiRequest<{ status: string; data: Enable2FAData }>('/console/auth/2fa/enable', {
    method: 'POST',
  });
  return raw.data;
}

/**
 * Verify 2FA setup with authenticator code
 */
export async function verify2FASetup(code: string): Promise<void> {
  await apiRequest<{ status: string }>('/console/auth/2fa/verify', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

/**
 * Disable 2FA — requires password and valid 2FA code
 */
export async function disable2FA(password: string, code: string): Promise<void> {
  await apiRequest<{ status: string }>('/console/auth/2fa/disable', {
    method: 'POST',
    body: JSON.stringify({ password, code }),
  });
}

// ============================================================================
// Contributions API
// ============================================================================

// GeoJSONGeometry is already defined above

export interface ContributionRequest {
  name: string;
  category: string;
  feature_type?: string;
  geometry: GeoJSONGeometry;
  address?: string;
  addr_street?: string;
  addr_suburb?: string;
  addr_city?: string;
  addr_district?: string;
  addr_region?: string;
  phone?: string;
  website?: string;
  description?: string;
  photos?: string[];
  building_units?: { label: string; floor: number }[];
  extra_fields?: Record<string, unknown>;
  campaign_id?: string;
  zone_id?: string;
}

export interface ContributionSummary {
  id: string;
  name: string;
  category: string;
  feature_type?: string;
  status: 'pending' | 'approved' | 'rejected';
  submitted_at: string;
  reviewed_at?: string;
  place_id?: string;
  points_awarded?: number | null;
  suggested_points?: number | null;
  suggested_activity_label?: string | null;
  submission_kind_label?: string | null;
}

export interface ContributionDetail extends ContributionSummary {
  geometry: GeoJSONGeometry;
  address?: string;
  addr_street?: string;
  addr_suburb?: string;
  addr_city?: string;
  addr_district?: string;
  addr_region?: string;
  phone?: string;
  website?: string;
  description?: string;
  photos: string[];
  reviewer_feedback?: string;
  estimated_review_time: string;
  extra_fields?: Record<string, unknown>;
  building_units?: { label: string; floor: number }[];
  suggested_activity?: string | null;
  submission_kind?: string | null;
  is_enrichment?: boolean | null;
  enrich_target_location_id?: string | null;
}

export interface ContributionEarningsMonthItem {
  month: string;
  month_label: string;
  approved_count: number;
  approved_points: number;
}

export interface ContributionEarningsActivityItem {
  activity: string;
  label: string;
  count: number;
  points: number;
}

export interface ContributionStatusCounts {
  all: number;
  pending: number;
  approved: number;
  rejected: number;
}

export interface UserContributionEarnings {
  year: number;
  month?: string | null;
  month_label?: string | null;
  approved_count: number;
  approved_points: number;
  pending_count: number;
  pending_suggested_points: number;
  by_month: ContributionEarningsMonthItem[];
  by_activity: ContributionEarningsActivityItem[];
  status_counts: ContributionStatusCounts;
}

export interface UserContributionEarningsResponse {
  status: string;
  year: number;
  month?: string | null;
  month_label?: string | null;
  approved_count: number;
  approved_points: number;
  pending_count: number;
  pending_suggested_points: number;
  by_month: ContributionEarningsMonthItem[];
  by_activity: ContributionEarningsActivityItem[];
  status_counts: ContributionStatusCounts;
}

export interface ContributionResponse {
  status: string;
  contribution: ContributionDetail;
}

export interface ContributionsListResponse {
  status: string;
  contributions: ContributionSummary[];
}

/**
 * Submit a new contribution
 */
export async function submitContribution(data: ContributionRequest): Promise<ContributionDetail> {
  const response = await apiRequest<ContributionResponse>('/map/contributions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.contribution;
}

export interface ListContributionsParams {
  status?: string;
  reviewedYear?: number;
  reviewedMonth?: string;
  featureType?: string;
}

/**
 * List user's contributions
 */
export async function listContributions(params?: ListContributionsParams | string): Promise<ContributionSummary[]> {
  const resolved: ListContributionsParams =
    typeof params === 'string' ? { status: params } : params ?? {};
  const search = new URLSearchParams();
  if (resolved.status && resolved.status !== 'all') search.set('status', resolved.status);
  if (resolved.reviewedMonth) search.set('reviewed_month', resolved.reviewedMonth);
  else if (resolved.reviewedYear) search.set('reviewed_year', String(resolved.reviewedYear));
  if (resolved.featureType) search.set('feature_type', resolved.featureType);
  const qs = search.toString();
  const response = await apiRequest<ContributionsListResponse>(`/map/contributions${qs ? `?${qs}` : ''}`);
  return response.contributions;
}

/**
 * Contributor earnings for a calendar year (optional month).
 */
export async function getContributionEarnings(
  year: number,
  month?: string,
): Promise<UserContributionEarnings> {
  const search = new URLSearchParams({ year: String(year) });
  if (month) search.set('month', month);
  const response = await apiRequest<UserContributionEarningsResponse>(`/map/contributions/earnings?${search}`);
  return response;
}

/**
 * Get contribution details (requires auth; admins can view any, users their own)
 */
export async function getContribution(contributionId: string): Promise<ContributionDetail> {
  const response = await apiRequest<ContributionResponse>(`/map/contributions/${contributionId}`);
  return response.contribution;
}

/**
 * Update a pending contribution
 */
export async function updateContribution(contributionId: string, data: ContributionRequest): Promise<ContributionDetail> {
  const response = await apiRequest<ContributionResponse>(`/map/contributions/${contributionId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return response.contribution;
}

/**
 * Delete a pending contribution
 */
export async function deleteContribution(contributionId: string): Promise<void> {
  await apiRequest<{ status: string; message: string }>(`/map/contributions/${contributionId}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Map Feature Enrichment
// ============================================================================

export interface MapFeatureResult {
  feature_type: string;
  table_id: number;
  location_id?: string;
  name?: string;
  category?: string;
  geometry: GeoJSONGeometry;
  distance_m: number;
  details: Record<string, unknown>;
}

/**
 * Find the nearest map feature of a given type near a clicked point.
 */
export async function findNearestFeature(
  lat: number,
  lng: number,
  featureType: string,
  radius: number = 50,
): Promise<MapFeatureResult | null> {
  const params = new URLSearchParams({
    lat: lat.toString(),
    lng: lng.toString(),
    feature_type: featureType,
    radius: radius.toString(),
  });
  const response = await apiRequest<{ status: string; feature: MapFeatureResult | null }>(
    `/map/features/nearest?${params}`,
  );
  return response.feature;
}

export type EnclosureDetectMode = 'point' | 'boundary_roads';

export type EnclosureOutcome = 'existing_polygon' | 'derived_candidates' | 'none';

export interface EnclosureExistingMatch {
  feature_type: string;
  table_id: number;
  name?: string | null;
  category?: string | null;
  area_sqm: number;
  geometry: GeoJSONGeometry;
}

export interface EnclosureCandidate {
  candidate_id: string;
  area_sqm: number;
  boundary_source: 'polygonize';
  geometry: GeoJSONGeometry;
}

export interface EnclosureDetectResponse {
  status: 'ok';
  outcome: EnclosureOutcome;
  message?: string | null;
  existing?: EnclosureExistingMatch | null;
  candidates: EnclosureCandidate[];
}

export interface RoadSnapCandidateDto {
  source?: 'road' | 'contribution';
  road_id?: number | null;
  contribution_id?: string | null;
  location_id?: string | null;
  name?: string | null;
  road_type?: string | null;
  endpoint: string;
  snap_lng: number;
  snap_lat: number;
  distance_m: number;
  layer?: number | null;
  eligible: boolean;
  ineligible_reason?: string | null;
}

export interface MotorwayLinkStatusDto {
  status: 'verified' | 'unverified' | 'no_link_nearby';
  message: string;
  verified: boolean;
  link_road_id?: number | null;
  distance_m?: number | null;
}

export async function fetchMotorwayLinkStatus(
  lng: number,
  lat: number,
  radiusM = 35,
): Promise<MotorwayLinkStatusDto> {
  const params = new URLSearchParams({
    lng: String(lng),
    lat: String(lat),
    radius_m: String(radiusM),
  });
  return apiRequest<MotorwayLinkStatusDto>(`/map/motorway-junction/link-status?${params}`);
}

export async function fetchRoadSnapCandidates(
  lng: number,
  lat: number,
  radiusM = 8,
  options?: { includeMyPending?: boolean; campaignId?: string | null },
): Promise<RoadSnapCandidateDto[]> {
  const params = new URLSearchParams({
    lng: String(lng),
    lat: String(lat),
    radius_m: String(radiusM),
  });
  if (options?.includeMyPending === false) {
    params.set('include_my_pending', 'false');
  }
  if (options?.campaignId) {
    params.set('campaign_id', options.campaignId);
  }
  const res = await apiRequest<{ status: string; candidates: RoadSnapCandidateDto[] }>(
    `/map/roads/snap-candidates?${params}`,
  );
  return res.candidates ?? [];
}

export async function fetchPendingSurveyGeometries(
  bbox: { west: number; south: number; east: number; north: number },
  campaignId?: string | null,
): Promise<GeoJSON.FeatureCollection> {
  if (!shouldFetchPendingSurveyGeometries(bbox)) {
    return { type: 'FeatureCollection', features: [] };
  }
  const params = new URLSearchParams({
    west: String(bbox.west),
    south: String(bbox.south),
    east: String(bbox.east),
    north: String(bbox.north),
  });
  if (campaignId) {
    params.set('campaign_id', campaignId);
  }
  const res = await apiRequest<{
    status: string;
    type: 'FeatureCollection';
    features: GeoJSON.Feature[];
  }>(`/map/contributions/pending-survey-geometries?${params}`);
  return { type: 'FeatureCollection', features: res.features ?? [] };
}

export async function detectEnclosure(params: {
  mode: EnclosureDetectMode;
  lng: number;
  lat: number;
  road_ids?: number[];
  search_radius_m?: number;
}): Promise<EnclosureDetectResponse> {
  return apiRequest<EnclosureDetectResponse>('/map/enclosure/detect', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

/**
 * Find an existing feature by exact name (optionally near a point).
 */
export async function matchFeatureByName(
  name: string,
  featureType: string,
  lat?: number,
  lng?: number,
  radiusM: number = 20000,
): Promise<MapFeatureResult | null> {
  const params = new URLSearchParams({
    name: name.trim(),
    feature_type: featureType,
    radius_m: radiusM.toString(),
  });
  if (lat != null && lng != null) {
    params.set('lat', lat.toString());
    params.set('lng', lng.toString());
  }
  const response = await apiRequest<{ status: string; feature: MapFeatureResult | null }>(
    `/map/features/match?${params}`,
  );
  return response.feature;
}


// ============================================================================
// Add Missing Place
// ============================================================================

export interface AddMissingPlaceRequest {
  name: string;
  place_type: 'home' | 'business' | 'other';
  latitude: number;
  longitude: number;
  street?: string;
  suburb?: string;
  city?: string;
  district?: string;
  region?: string;
  phone?: string;
  description?: string;
}

export interface PendingPlaceDetail {
  id: string;
  name: string;
  place_type: 'home' | 'business' | 'other';
  latitude: number;
  longitude: number;
  street?: string;
  suburb?: string;
  city?: string;
  district?: string;
  region?: string;
  postcode?: string;
  phone?: string;
  description?: string;
  status: 'pending' | 'verified' | 'rejected';
  formatted_address?: string;
  created_at: string;
  verified_at?: string;
  rejection_reason?: string;
}

export interface PendingPlaceSummary {
  id: string;
  name: string;
  place_type: 'home' | 'business' | 'other';
  postcode?: string;
  status: 'pending' | 'verified' | 'rejected';
  formatted_address?: string;
  created_at: string;
}

/**
 * Submit a missing place. Returns the place with an instantly generated postcode.
 */
export async function addMissingPlace(data: AddMissingPlaceRequest): Promise<PendingPlaceDetail> {
  const response = await apiRequest<{ status: string; place: PendingPlaceDetail }>('/map/places', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.place;
}

/**
 * List the current user's submitted places.
 */
export async function listMyPlaces(status?: string): Promise<PendingPlaceSummary[]> {
  const params = status ? `?status=${status}` : '';
  const response = await apiRequest<{ status: string; places: PendingPlaceSummary[] }>(
    `/map/places/mine${params}`,
  );
  return response.places;
}

/**
 * Get details of a specific pending place.
 */
export async function getPendingPlace(placeId: string): Promise<PendingPlaceDetail> {
  const response = await apiRequest<{ status: string; place: PendingPlaceDetail }>(
    `/map/places/${placeId}`,
  );
  return response.place;
}

// --- Campaign field workspace (public map). Implemented in API as /map/campaigns — aliases /console/campaigns field routes. ---

const MAP_CAMPAIGNS_PREFIX = '/map/campaigns';

export interface CampaignDetailMap {
  id: string;
  name: string;
  description?: string | null;
  district_name: string | null;
  status: string;
  zone_count: number;
  collected_count: number;
  /** Owning organization logo (URL or data URL), when configured. */
  organization_logo_url?: string | null;
  /** Campaign policy for session start (registered device / external GNSS). */
  field_device_requirement?: 'none' | 'assigned_device' | 'external_gnss';
}

export interface CampaignMapContextViewer {
  campaign_team_role?: string | null;
  organization_role?: string | null;
  is_staff?: boolean;
}

export async function getCampaignForMap(campaignId: string): Promise<{
  campaign: CampaignDetailMap;
  viewer: CampaignMapContextViewer | null;
}> {
  const res = await apiRequest<{
    status: string;
    campaign: CampaignDetailMap;
    viewer?: CampaignMapContextViewer | null;
  }>(`${MAP_CAMPAIGNS_PREFIX}/${campaignId}/map-context`);
  return { campaign: res.campaign, viewer: res.viewer ?? null };
}

export async function fetchCampaignZonesGeoJSON(campaignId: string): Promise<GeoJSON.FeatureCollection> {
  return apiRequest<GeoJSON.FeatureCollection>(`${MAP_CAMPAIGNS_PREFIX}/${campaignId}/zones/geojson`);
}

/** Claim an unassigned zone as the signed-in user (team / org field roles). */
export async function claimCampaignZone(campaignId: string, zoneId: string): Promise<{ status: string; message: string }> {
  return apiRequest<{ status: string; message: string }>(
    `${MAP_CAMPAIGNS_PREFIX}/${campaignId}/zones/${encodeURIComponent(zoneId)}/claim`,
    { method: 'POST' },
  );
}

export async function fetchCampaignCollectionsGeoJSON(
  campaignId: string,
  limit = 500,
  since?: string | null,
): Promise<GeoJSON.FeatureCollection> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (since) params.set('since', since);
  return apiRequest<GeoJSON.FeatureCollection>(
    `${MAP_CAMPAIGNS_PREFIX}/${campaignId}/collections/geojson?${params.toString()}`,
  );
}

/** WebSocket URL for campaign live stream (JWT via query; same auth rules as map overlays). */
export function campaignStreamWebSocketUrl(campaignId: string, token: string): string {
  const u = new URL(API_URL);
  u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
  const basePath = u.pathname.replace(/\/$/, '');
  u.pathname = `${basePath}/ws/campaigns/${encodeURIComponent(campaignId)}/stream`;
  u.search = `?token=${encodeURIComponent(token)}`;
  return u.toString();
}

export interface MyCampaignAssignmentRow {
  id: string;
  name: string;
  district_name: string | null;
  status: string;
}

export interface MyAssignedZoneRow {
  id: string;
  name: string | null;
  color: string | null;
  status: string;
  campaign_id: string | null;
  campaign_name: string | null;
  estimated_buildings?: number;
  area_km2?: number | null;
  features_collected?: number;
}

export async function fetchMyAssignedZones(): Promise<MyAssignedZoneRow[]> {
  const res = await apiRequest<{ status: string; zones: MyAssignedZoneRow[]; total: number }>(
    `${MAP_CAMPAIGNS_PREFIX}/my/zones`,
  );
  return res.zones ?? [];
}

export async function fetchMyCampaignAssignments(): Promise<MyCampaignAssignmentRow[]> {
  const res = await apiRequest<{
    status: string;
    campaigns: MyCampaignAssignmentRow[];
    total: number;
  }>(`${MAP_CAMPAIGNS_PREFIX}/my/assignments`);
  return res.campaigns ?? [];
}

export interface FieldDeviceSummary {
  id: string;
  device_tag: string;
  device_model: string | null;
  status: string;
  gnss_receiver: string | null;
  gnss_connection: string | null;
  correction_source: string | null;
  assigned_to_id: number | null;
  assigned_to_name: string | null;
  last_seen_at: string | null;
  last_battery_pct: number | null;
  last_storage_free_mb: number | null;
  mdm_enrolled: boolean;
  /** From mobile heartbeat — pending offline upload queue size. */
  pending_outbox_count?: number | null;
  last_sync_attempt_at?: string | null;
  /** Last heartbeat: internal | external */
  last_gnss_position_source?: string | null;
  last_gnss_link_ok?: boolean | null;
}

export async function fetchCampaignDevices(campaignId: string): Promise<FieldDeviceSummary[]> {
  const res = await apiRequest<{ devices: FieldDeviceSummary[]; total: number }>(
    `${MAP_CAMPAIGNS_PREFIX}/${campaignId}/devices`,
  );
  return res.devices || [];
}

export interface CampaignSessionRow {
  id: string;
  status: string;
  started_at: string;
  ended_at: string | null;
  features_collected: number;
  user_id: number | null;
  user_name: string | null;
  offline_synced_at: string | null;
  task_id: string | null;
  field_device_id?: string | null;
  /** Horizontal accuracy of session start fix (meters), if reported. */
  start_location_accuracy_m?: number | null;
  /** Attested coordinate pipeline (browser_geolocation, external_receiver_nmea, …). */
  position_source?: string | null;
  /** Structured client report of hardware used (may be present without field_device_id). */
  collection_device?: Record<string, unknown> | null;
  /** Present when loaded via my/active-session (session may belong to another campaign). */
  campaign_id?: string | null;
  campaign_name?: string | null;
  focused_zone_id?: string | null;
}

export interface MyActiveCampaignSessionResponse {
  status: string;
  session: CampaignSessionRow | null;
  campaign_id: string | null;
  campaign_name: string | null;
  focused_zone_id: string | null;
}

/** Public map / PWA uses browser Geolocation only — cannot satisfy external_gnss campaigns. */
export const POSITION_SOURCE_BROWSER_GEOLOCATION = 'browser_geolocation';

/** Optional: report device used for collection even when not a registered org asset. */
export interface SessionCollectionDevicePayload {
  is_registered_asset?: boolean;
  device_label?: string;
  device_model?: string;
  manufacturer?: string;
  os_name?: string;
  os_version?: string;
  app_version?: string;
  gnss_source?: string;
  gnss_receiver_label?: string;
  gnss_connection?: string;
  client_user_agent?: string;
  /** Horizontal accuracy at session start (meters). */
  horizontal_accuracy_m?: number;
}

export async function fetchMyActiveCampaignSession(): Promise<MyActiveCampaignSessionResponse> {
  return apiRequest<MyActiveCampaignSessionResponse>(`${MAP_CAMPAIGNS_PREFIX}/my/active-session`);
}

export async function fetchCampaignSessions(
  campaignId: string,
  userId?: number,
): Promise<CampaignSessionRow[]> {
  const q = userId != null ? `?user_id=${userId}` : '';
  const res = await apiRequest<{ sessions: CampaignSessionRow[]; total: number }>(
    `${MAP_CAMPAIGNS_PREFIX}/${campaignId}/sessions${q}`,
  );
  return res.sessions || [];
}

/** True when registry GNSS label denotes external hardware (matches server policy). */
export function gnssReceiverImpliesExternal(receiver: string | null | undefined): boolean {
  if (!receiver?.trim()) return false;
  const norm = receiver.trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (['internal', 'phone', 'device', 'tablet', 'integrated', 'agps'].includes(norm)) return false;
  if (norm.startsWith('phone') || norm.startsWith('internal')) return false;
  return true;
}

export async function startCampaignSession(
  campaignId: string,
  body: {
    start_lat?: number;
    start_lng?: number;
    start_location_accuracy_m?: number;
    task_id?: string;
    device_info?: Record<string, unknown>;
    field_device_id?: string;
    /** Required for external_gnss campaigns on native clients (not browser_geolocation). */
    position_source?: string;
    device_preflight?: Record<string, unknown>;
    collection_device?: SessionCollectionDevicePayload;
  },
): Promise<CampaignSessionRow[]> {
  const res = await apiRequest<{ sessions: CampaignSessionRow[] }>(
    `${MAP_CAMPAIGNS_PREFIX}/${campaignId}/sessions/start`,
    { method: 'POST', body: JSON.stringify(body) },
  );
  return res.sessions || [];
}

export async function endCampaignSession(
  campaignId: string,
  sessionId: string,
  body?: { end_lat?: number; end_lng?: number; distance_covered_km?: number },
): Promise<void> {
  await apiRequest(`${MAP_CAMPAIGNS_PREFIX}/${campaignId}/sessions/${sessionId}/end`, {
    method: 'POST',
    body: JSON.stringify(body || {}),
  });
}

// ============================================================================
// Transport Layer
// ============================================================================

/**
 * Fetch all transport routes as a GeoJSON FeatureCollection.
 * Each feature has a `color` property for per-operator rendering.
 */
export async function getTransportRoutes(type?: string): Promise<GeoJSON.FeatureCollection> {
  const params = type ? `?type=${encodeURIComponent(type)}` : '';
  const res = await apiRequest<GeoJSON.FeatureCollection>(`/map/transport/routes${params}`);
  return res;
}

/**
 * Fetch transport stops near a location as a GeoJSON FeatureCollection.
 */
export async function getTransportStops(
  lat?: number,
  lng?: number,
  radius?: number,
): Promise<GeoJSON.FeatureCollection> {
  const params = new URLSearchParams();
  if (lat != null) params.set('lat', lat.toString());
  if (lng != null) params.set('lng', lng.toString());
  if (radius != null) params.set('radius', radius.toString());
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await apiRequest<GeoJSON.FeatureCollection>(`/map/transport/stops${qs}`);
  return res;
}

// ============================================================================
// Transport Console (enterprise)
// ============================================================================

export interface TransportOperatorSummary {
  id: number;
  name: string;
  slug: string;
  color: string;
}

export interface TransportRouteSummary {
  id: number;
  route_name: string;
  type: string;
  visibility: 'private' | 'under_review' | 'approved' | 'rejected';
  operator: string;
  created_at: string | null;
}

export async function listMyTransportOperators(): Promise<TransportOperatorSummary[]> {
  const res = await apiRequest<{ status: string; operators: TransportOperatorSummary[] }>(
    '/console/transport/operators',
    { method: 'GET' },
    true,
  );
  return res.operators;
}

export async function createTransportOperator(
  name: string,
  color: string,
  opts?: { description?: string; website?: string; contact_email?: string; phone?: string },
): Promise<TransportOperatorSummary> {
  const params = new URLSearchParams({ name, color, ...opts as Record<string, string> });
  const res = await apiRequest<{ status: string; operator: TransportOperatorSummary }>(
    `/console/transport/operators?${params.toString()}`,
    { method: 'POST' },
    true,
  );
  return res.operator;
}

export async function startTransportSession(operatorId: number, routeId?: number): Promise<{ id: number; operator_id: number; started_at: string }> {
  const params = new URLSearchParams({ operator_id: operatorId.toString() });
  if (routeId != null) params.set('route_id', routeId.toString());
  const res = await apiRequest<{ status: string; session: { id: number; operator_id: number; started_at: string } }>(
    `/console/transport/sessions?${params.toString()}`,
    { method: 'POST' },
    true,
  );
  return res.session;
}

export async function logTransportStop(
  sessionId: number,
  lat: number,
  lng: number,
  name?: string,
  stopType?: string,
): Promise<{ id: number; name: string; lat: number; lng: number; sequence: number }> {
  const params = new URLSearchParams({ lat: lat.toString(), lng: lng.toString() });
  if (name) params.set('name', name);
  if (stopType) params.set('stop_type', stopType);
  const res = await apiRequest<{ status: string; stop: { id: number; name: string; lat: number; lng: number; sequence: number } }>(
    `/console/transport/sessions/${sessionId}/stops?${params.toString()}`,
    { method: 'POST' },
    true,
  );
  return res.stop;
}

export async function endTransportSession(
  sessionId: number,
  routeName?: string,
  buildRoute?: boolean,
): Promise<{ id: number; stop_count: number; route_id: number | null }> {
  const params = new URLSearchParams({ build_route: buildRoute !== false ? 'true' : 'false' });
  if (routeName) params.set('route_name', routeName);
  const res = await apiRequest<{ status: string; session: { id: number; stop_count: number; route_id: number | null } }>(
    `/console/transport/sessions/${sessionId}/end?${params.toString()}`,
    { method: 'POST' },
    true,
  );
  return res.session;
}

export async function listMyTransportRoutes(): Promise<TransportRouteSummary[]> {
  const res = await apiRequest<{ status: string; routes: TransportRouteSummary[] }>(
    '/console/transport/routes',
    { method: 'GET' },
    true,
  );
  return res.routes;
}

export async function submitTransportRouteForReview(routeId: number): Promise<void> {
  await apiRequest(
    `/console/transport/routes/${routeId}/submit-for-review`,
    { method: 'POST' },
    true,
  );
}

// ============================================================================
// In-app notifications (shared with developer console)
// ============================================================================

export interface AppNotification {
  id: number;
  title: string;
  message: string;
  notification_type: string;
  is_read: boolean;
  action_url: string | null;
  created_at: string;
}

export async function fetchUnreadContributionNotifications(): Promise<AppNotification[]> {
  const res = await apiRequest<{
    status: string;
    data: { items: AppNotification[] };
  }>('/console/notifications?notification_type=contribution&unread_only=true&page_size=10');
  return res.data?.items ?? [];
}

export async function markNotificationsRead(notificationIds: number[]): Promise<void> {
  if (!notificationIds.length) return;
  await apiRequest('/console/notifications/mark-read', {
    method: 'POST',
    body: JSON.stringify({ notification_ids: notificationIds }),
  });
}
